import {
  Box,
  Heading,
  Button,
  Badge,
  Text,
  HStack,
  VStack,
  useColorModeValue,
  Alert,
  AlertIcon,
  useDisclosure,
  Icon,
  Tooltip,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiEdit, FiLock, FiPlus, FiShield, FiUsers } from 'react-icons/fi'
import { useAdminUsers, useAdminRoles } from '@/hooks/useAdminData'
import { AdminUser, AdminRole } from '@/types'
import { useAuth } from '@/contexts/AuthContext'
import { AdminUserModal } from '@/components/adminUsers/AdminUserModal'
import { PasswordChangeModal } from '@/components/adminUsers/PasswordChangeModal'
import { AdminMfaResetModal } from '@/components/adminUsers/AdminMfaResetModal'
import { DataTable } from '@/components/common/DataTable'
import {
  ColumnConfig,
  FilterConfig,
  PaginationConfig,
  ActionButton,
} from '@/components/common/DataTable/types'
import { formatDate } from '@/utils/formatUtils'
import { useAdminTabQuery } from '@/navigation/adminTabQuery'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { withNetworkStorageKey } from '@/utils/storageKeys'
import { canResetAdminMfa } from './adminMfaResetVisibility'
import {
  canCreateAdminUser,
  canEditAdminUser,
  canResetAdminUserPassword,
} from './adminUserManagementPermissions'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'

const ADMIN_USERS_TABS = ['users', 'roles'] as const

export function AdminUsersPage() {
  const { t } = useTranslation()
  const { user: currentUser } = useAuth()
  const { currentEnv } = useNetworkEnvironment()
  const TAB_STORAGE_KEY = useMemo(
    () => withNetworkStorageKey('admin_portal_admin_users_tab', currentEnv),
    [currentEnv]
  )
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [role, setRole] = useState('')
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [passwordUser, setPasswordUser] = useState<AdminUser | null>(null)
  const [mfaResetUser, setMfaResetUser] = useState<AdminUser | null>(null)
  const { activeTabIndex: tabIndex, handleTabChange } = useAdminTabQuery({
    tabs: ADMIN_USERS_TABS,
    defaultTab: 'users',
    storageKey: TAB_STORAGE_KEY,
  })
  const limit = 20

  const { data, isLoading, error, refetch, isFetching } = useAdminUsers({
    page,
    limit,
    search,
    role,
  })
  const {
    data: rolesData,
    isLoading: isRolesLoading,
    error: rolesError,
    refetch: refetchRoles,
    isFetching: isFetchingRoles,
  } = useAdminRoles({
    enabled: tabIndex === 1,
  })

  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure()

  const {
    isOpen: isMfaResetOpen,
    onOpen: onMfaResetOpen,
    onClose: onMfaResetClose,
  } = useDisclosure()

  const {
    isOpen: isPasswordModalOpen,
    onOpen: onPasswordModalOpen,
    onClose: onPasswordModalClose,
  } = useDisclosure()

  const textColor = useColorModeValue('gray.600', 'gray.300')

  // Permission checks
  const canCreateUser = canCreateAdminUser(currentUser)

  const getRoleColor = (roleId: string, roleCode?: string) => {
    // Check both role_id and role_code formats
    const role = roleId || roleCode || ''

    // Map colors based on role (supporting both formats)
    if (role === 'arl_superadmin' || role === 'superadmin') return 'red'
    if (role === 'arl_admin' || role === 'admin') return 'blue'
    if (role === 'arl_operator' || role === 'operator' || role === 'moderator')
      return 'green'
    if (role === 'arl_support' || role === 'support') return 'purple'
    if (role === 'arl_viewer' || role === 'viewer') return 'gray'

    return 'gray'
  }

  // Define column configuration for DataTable
  const columns: ColumnConfig<AdminUser>[] = [
    {
      key: 'id',
      title: t('adminUsers.table.userId'),
      width: '18%',
      render: value => (
        <Text fontSize="sm" fontFamily="mono">
          {value}
        </Text>
      ),
    },
    {
      key: 'email',
      title: t('adminUsers.table.email'),
      width: '20%',
    },
    {
      key: 'username',
      title: t('adminUsers.table.username'),
      width: '15%',
    },
    {
      key: 'role_id',
      title: t('adminUsers.table.role'),
      width: '10%',
      render: (_, row) => (
        <Badge colorScheme={getRoleColor(row.role_id, row.role_code)}>
          {row.role_code || row.role_id?.replace('arl_', '')}
        </Badge>
      ),
    },
    {
      key: 'is_active',
      title: t('adminUsers.table.status'),
      width: '10%',
      render: value => (
        <Badge colorScheme={value ? 'green' : 'red'}>
          {value
            ? t('adminUsers.status.active')
            : t('adminUsers.status.inactive')}
        </Badge>
      ),
    },
    {
      key: 'is_mfa_enabled',
      title: t('adminUsers.table.mfa'),
      width: '8%',
      render: value => (
        <Badge colorScheme={value ? 'green' : 'orange'}>
          {value ? t('adminUsers.mfa.enabled') : t('adminUsers.mfa.disabled')}
        </Badge>
      ),
    },
    {
      key: 'last_login_at',
      title: t('adminUsers.table.lastLogin'),
      width: '12%',
      render: value => (
        <Text fontSize="sm">
          {value ? formatDate(value) : t('common.never')}
        </Text>
      ),
    },
    {
      key: 'created_at',
      title: t('adminUsers.table.created'),
      width: '10%',
      render: value => (
        <Text fontSize="sm">{value ? formatDate(value) : t('common.na')}</Text>
      ),
    },
  ]

  // Define filter configuration for DataTable
  const filters: FilterConfig[] = [
    {
      key: 'search',
      type: 'text',
      label: t('adminUsers.searchPlaceholder'),
      placeholder: t('adminUsers.searchPlaceholder'),
      width: '300px',
    },
    {
      key: 'role',
      type: 'select',
      label: t('adminUsers.filterByRole'),
      placeholder: t('adminUsers.filterByRole'),
      width: '200px',
      options: [
        { value: 'superadmin', label: t('adminUsers.roles.superadmin') },
        { value: 'admin', label: t('adminUsers.roles.admin') },
        { value: 'moderator', label: t('adminUsers.roles.moderator') },
      ],
    },
  ]

  // Define pagination configuration
  const paginationConfig: PaginationConfig = {
    page,
    pageSize: limit,
    total: data?.pagination?.total || 0,
    pageSizeOptions: [10, 20, 50],
  }

  const roleColumns: ColumnConfig<AdminRole>[] = [
    { key: 'id', title: t('adminUsers.roles.roleId'), width: '22%' },
    { key: 'code', title: t('adminUsers.roles.roleCode'), width: '18%' },
    { key: 'name', title: t('adminUsers.roles.roleName'), width: '18%' },
    { key: 'level', title: t('adminUsers.roles.roleLevel'), width: '12%' },
    {
      key: 'is_active',
      title: t('adminUsers.roles.active'),
      width: '10%',
      render: value => (
        <Badge
          colorScheme={
            value === true || value === 1 || value === '1' || value === 'true'
              ? 'green'
              : 'red'
          }
        >
          {value === true || value === 1 || value === '1' || value === 'true'
            ? t('adminUsers.status.active')
            : t('adminUsers.status.inactive')}
        </Badge>
      ),
    },
    {
      key: 'description',
      title: t('adminUsers.roles.description'),
      width: '20%',
      render: value => (
        <Text fontSize="sm" color="gray.600" noOfLines={2}>
          {value || t('common.na')}
        </Text>
      ),
    },
  ]

  // Get actions for each row
  const getRowActions = (user: AdminUser): ActionButton[] => {
    const actions: ActionButton[] = []

    if (canEditAdminUser(currentUser, user)) {
      actions.push({
        label: '',
        icon: <FiEdit />,
        tooltip: t('common.edit'),
        onClick: () => handleEditUser(user),
        size: 'xs',
        variant: 'ghost',
      })
    }

    if (canResetAdminUserPassword(currentUser, user)) {
      actions.push({
        label: '',
        icon: <FiLock />,
        tooltip: t('adminUsers.changePassword'),
        onClick: () => handleChangePassword(user),
        size: 'xs',
        variant: 'ghost',
      })
    }

    if (canResetAdminMfa(currentUser, user)) {
      actions.push({
        label: '',
        icon: <FiShield />,
        tooltip: t('adminUsers.mfaReset.action'),
        onClick: () => {
          setMfaResetUser(user)
          onMfaResetOpen()
        },
        size: 'xs',
        variant: 'ghost',
        colorScheme: 'red',
      })
    }
    return actions
  }

  // Handle filter changes
  const handleFiltersChange = (newFilters: Record<string, any>) => {
    setSearch(newFilters.search || '')
    setRole(newFilters.role || '')
    setPage(1)
  }

  // Handle pagination changes
  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handlePageSizeChange = (_newSize: number) => {
    // Page size is fixed.
  }

  const handleAddUser = () => {
    setSelectedUser(null)
    onModalOpen()
  }

  const handleEditUser = (user: AdminUser) => {
    setSelectedUser(user)
    onModalOpen()
  }

  const handleChangePassword = (user: AdminUser) => {
    setPasswordUser(user)
    onPasswordModalOpen()
  }

  const handleModalClose = () => {
    setSelectedUser(null)
    onModalClose()
  }

  const handlePasswordModalClose = () => {
    setPasswordUser(null)
    onPasswordModalClose()
  }

  const handleSuccess = () => {
    refetch()
  }

  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between" align="flex-start">
          <Box>
            <Heading size="lg" mb={2}>
              {t('adminUsers.title')}
            </Heading>
            <Text color={textColor}>{t('adminUsers.description')}</Text>
          </Box>
          <RefreshIconButton
            label={t('common.refresh')}
            tooltipPlacement="left"
            size="md"
            onClick={() => (tabIndex === 0 ? refetch() : refetchRoles())}
            isLoading={tabIndex === 0 ? isFetching : isFetchingRoles}
          />
        </HStack>

        <Tabs
          index={tabIndex}
          onChange={handleTabChange}
          colorScheme="blue"
          isLazy
          lazyBehavior="keepMounted"
        >
          <TabList
            overflowX={{ base: 'auto', md: 'visible' }}
            overflowY="hidden"
          >
            <Tab
              aria-label={t('adminUsers.tab.users', 'Admin Users')}
              minW="56px"
              justifyContent="center"
            >
              <Tooltip
                label={t('adminUsers.tab.users', 'Admin Users')}
                hasArrow
              >
                <Box display="flex" alignItems="center">
                  <Icon as={FiUsers} boxSize={6} />
                </Box>
              </Tooltip>
            </Tab>
            <Tab
              aria-label={t('adminUsers.tab.roles', 'Admin Roles')}
              minW="56px"
              justifyContent="center"
            >
              <Tooltip
                label={t('adminUsers.tab.roles', 'Admin Roles')}
                hasArrow
              >
                <Box display="flex" alignItems="center">
                  <Icon as={FiShield} boxSize={6} />
                </Box>
              </Tooltip>
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0}>
              <Box>
                {canCreateUser && (
                  <Box mb={4}>
                    <Button
                      colorScheme="green"
                      leftIcon={<FiPlus />}
                      onClick={handleAddUser}
                    >
                      {t('adminUsers.addAdminUser')}
                    </Button>
                  </Box>
                )}

                <DataTable<AdminUser>
                  data={data?.data || []}
                  loading={isLoading}
                  error={error}
                  columns={columns}
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  showFilterBar={true}
                  sortable={true}
                  pagination={paginationConfig}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  actions={getRowActions}
                  actionsColumnWidth="6%"
                  size="sm"
                  emptyMessage={t('adminUsers.noUsers')}
                  showExport={false}
                />
              </Box>
            </TabPanel>
            <TabPanel px={0}>
              {rolesError ? (
                <Alert status="error">
                  <AlertIcon />
                  {t('common.error')}
                </Alert>
              ) : (
                <DataTable<AdminRole>
                  data={rolesData?.data || []}
                  loading={isRolesLoading}
                  error={rolesError}
                  columns={roleColumns}
                  showFilterBar={false}
                  sortable={false}
                  pagination={undefined}
                  size="sm"
                  emptyMessage={t('adminUsers.roles.empty', 'No roles found')}
                  showExport={false}
                />
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>

      {/* Modals */}
      <AdminUserModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        user={selectedUser}
        onSuccess={handleSuccess}
      />

      <PasswordChangeModal
        isOpen={isPasswordModalOpen}
        onClose={handlePasswordModalClose}
        user={passwordUser}
      />

      <AdminMfaResetModal
        isOpen={isMfaResetOpen}
        onClose={() => {
          setMfaResetUser(null)
          onMfaResetClose()
        }}
        user={mfaResetUser}
        onSuccess={handleSuccess}
      />
    </Box>
  )
}
